import type { IconName } from '@customerio/pluma-icons';

/**
 * Display metadata for workflow action types.
 *
 * The canvas chrome reads the `name`, `icon`, `group` and `activePhraseShort`
 * fields, and whether the type shows a customer-given name on its card. The
 * action edit panel reads its title, the name rules, the name field's example
 * name and whether the channel needs setting up. Analytics reads the name it
 * reports each type by. Delivery metrics and beta flags are not here.
 */

/** Ember's `GroupName`: the add-actions panel categories. */
export type ActionGroup = 'messages' | 'data' | 'delays' | 'flow_control' | 'ai';

export type ActionDisplayInfo = {
  /** Human label for the type, e.g. `Time Delay`. */
  name: string;
  /** The name analytics reports the type by, e.g. `TF Branch` (Ember's `analyticsName`). */
  analyticsName: string;
  icon: IconName;
  /**
   * Category, which colors the card's stripe. Absent on the A/B test, whose
   * card has no stripe.
   */
  group?: ActionGroup;
  /**
   * Whether the card titles itself with the action's own name. A type that
   * takes no name (a time delay, an exit) always titles itself with `name`.
   */
  showsCustomName: boolean;
  /**
   * The verb in the card's preconditions summary: `Only send if customer meets
   * 2 conditions`. Empty for a type Ember gives no phrase, which then reads
   * `Only  if customer meets`, blank verb and all, as it does on the Ember card.
   */
  activePhraseShort: string;
  /** The edit panel's title for an action with no name of its own, tried before `name`. */
  editSidebarName?: string;
  /** The example name an empty name field shows. */
  placeholder?: string;
  /** `true` when the action must have a name, `false` when it may go without one. */
  nameRequired?: boolean;
  /** Whether the name may be left blank. */
  nameOptional?: boolean;
  /** Whether the action's channel has to be set up before the action can be edited. */
  requiresSetup?: boolean;
};

/**
 * Ember's `ActionType` union, less the trigger: every action type the table
 * describes, as a value so a wire `type` can be checked against it.
 */
export const KNOWN_ACTION_TYPES = [
  'conditional_wait_action',
  'delay_seconds_action',
  'delay_time_window_action',
  'grace_period_action',
  'randomized_delay_action',
  'email_action',
  'slack_action',
  'twilio_action',
  'sms_action',
  'whatsapp_action',
  'line_action',
  'inbox_action',
  'push_action',
  'live_notification_action',
  'urban_airship_action',
  'in_app_action',
  'webhook_action',
  'attribute_update_action',
  'static_seg_update_action',
  'collection_query_action',
  'query_objects_action',
  'create_event_action',
  'batch_update_action',
  'llm_action',
  'split_randomized_action',
  'conditional_branch_action',
  'multi_split_branch_action',
  'multi_lang_branch_action',
  'random_cohort_branch_action',
  'experiment_action',
  'exit_action',
] as const;

export type KnownActionType = (typeof KNOWN_ACTION_TYPES)[number];

const ACTION_INFO = {
  conditional_wait_action: {
    name: 'Wait Until…',
    analyticsName: 'Wait Until',
    icon: 'wait',
    group: 'delays',
    showsCustomName: true,
    activePhraseShort: 'wait',
    nameRequired: false,
    nameOptional: true,
  },
  delay_seconds_action: {
    name: 'Time Delay',
    analyticsName: 'Time Delay',
    icon: 'time-delay',
    group: 'delays',
    showsCustomName: false,
    activePhraseShort: 'wait',
    nameRequired: false,
  },
  delay_time_window_action: {
    name: 'Time Window',
    analyticsName: 'Time Window',
    icon: 'time-window',
    group: 'delays',
    showsCustomName: false,
    activePhraseShort: 'wait',
    nameRequired: false,
  },
  grace_period_action: {
    name: 'Grace Period',
    analyticsName: 'Grace Period',
    icon: 'time-delay',
    group: 'delays',
    showsCustomName: false,
    activePhraseShort: 'wait',
    nameRequired: false,
  },
  randomized_delay_action: {
    name: 'Randomized Delay',
    analyticsName: 'Randomized Delay',
    icon: 'time-delay',
    group: 'delays',
    showsCustomName: false,
    activePhraseShort: 'wait',
    nameRequired: false,
  },

  email_action: {
    name: 'Email',
    analyticsName: 'Email',
    icon: 'email',
    group: 'messages',
    showsCustomName: true,
    activePhraseShort: 'send',
    placeholder: 'Welcome Email',
    nameRequired: true,
  },
  slack_action: {
    name: 'Slack Message',
    analyticsName: 'Slack',
    icon: 'slack',
    group: 'messages',
    showsCustomName: true,
    activePhraseShort: 'send',
    placeholder: 'Sales Alert',
    nameRequired: true,
    requiresSetup: true,
  },
  twilio_action: {
    name: 'SMS',
    analyticsName: 'Twilio',
    icon: 'sms',
    group: 'messages',
    showsCustomName: true,
    activePhraseShort: 'send',
    placeholder: 'Account Activation SMS',
    nameRequired: true,
    requiresSetup: true,
  },
  sms_action: {
    name: 'SMS',
    analyticsName: 'SMS',
    icon: 'sms',
    group: 'messages',
    showsCustomName: true,
    activePhraseShort: 'send',
    placeholder: 'Account activation SMS',
    nameRequired: true,
    requiresSetup: true,
  },
  whatsapp_action: {
    name: 'WhatsApp',
    analyticsName: 'WhatsApp',
    icon: 'brand/whatsapp',
    group: 'messages',
    showsCustomName: true,
    activePhraseShort: 'send',
    nameRequired: true,
    requiresSetup: true,
  },
  line_action: {
    name: 'LINE Message',
    analyticsName: 'LINE',
    icon: 'brand/line',
    group: 'messages',
    showsCustomName: true,
    activePhraseShort: 'send',
    nameRequired: true,
    requiresSetup: true,
  },
  inbox_action: {
    name: 'Inbox Message',
    analyticsName: 'Inbox Message',
    icon: 'notifications',
    group: 'messages',
    showsCustomName: true,
    activePhraseShort: 'send',
    nameRequired: true,
  },
  push_action: {
    name: 'Push Notification',
    analyticsName: 'Push',
    icon: 'mobile',
    group: 'messages',
    showsCustomName: true,
    activePhraseShort: 'send',
    placeholder: 'Welcome Push Notification',
    nameRequired: true,
    requiresSetup: true,
  },
  live_notification_action: {
    name: 'Live Notification',
    analyticsName: 'Live Notification',
    icon: 'mobile',
    group: 'messages',
    showsCustomName: false,
    activePhraseShort: 'send',
  },
  urban_airship_action: {
    name: 'Airship',
    analyticsName: 'Airship',
    icon: 'mobile',
    group: 'messages',
    showsCustomName: true,
    activePhraseShort: 'send',
    placeholder: 'Welcome Push Notification',
    nameRequired: true,
  },
  in_app_action: {
    name: 'In-App Message',
    analyticsName: 'In-App',
    icon: 'in-app',
    group: 'messages',
    showsCustomName: true,
    activePhraseShort: 'send',
    placeholder: 'Welcome Message',
    nameRequired: true,
    requiresSetup: true,
  },

  webhook_action: {
    name: 'Webhook',
    analyticsName: 'Webhook',
    icon: 'webhook',
    group: 'data',
    showsCustomName: true,
    activePhraseShort: 'send',
    placeholder: 'New Signup Notification',
    nameRequired: true,
  },
  attribute_update_action: {
    name: 'Create or Update Person',
    analyticsName: 'Create or Update Person',
    icon: 'person',
    group: 'data',
    showsCustomName: true,
    activePhraseShort: 'create or update',
    editSidebarName: 'Create or Update Person',
    placeholder: "Update Customer's Anniversary",
    nameRequired: true,
  },
  static_seg_update_action: {
    name: 'Manual Segment Update',
    analyticsName: 'Manual Segment Update',
    icon: 'segment-manual',
    group: 'data',
    showsCustomName: true,
    activePhraseShort: 'update',
    nameRequired: false,
    nameOptional: true,
  },
  collection_query_action: {
    name: 'Query Collection',
    analyticsName: 'Query Collection Update',
    icon: 'database',
    group: 'data',
    showsCustomName: true,
    activePhraseShort: 'update',
    nameRequired: false,
    nameOptional: true,
  },
  query_objects_action: {
    name: 'Query Objects',
    analyticsName: 'Query Objects',
    icon: 'object',
    group: 'data',
    showsCustomName: true,
    activePhraseShort: 'query',
    nameRequired: false,
    nameOptional: true,
  },
  create_event_action: {
    name: 'Send Event',
    analyticsName: 'Create Event',
    icon: 'trigger-event',
    group: 'data',
    showsCustomName: true,
    activePhraseShort: 'send',
    nameRequired: true,
  },
  batch_update_action: {
    name: 'Batch Update',
    analyticsName: 'Batch Update',
    icon: 'person',
    group: 'data',
    showsCustomName: true,
    activePhraseShort: 'update',
    nameRequired: true,
  },
  llm_action: {
    name: 'Run LLM',
    analyticsName: 'Run LLM',
    icon: 'sparkle',
    group: 'data',
    showsCustomName: true,
    activePhraseShort: '',
    nameRequired: true,
  },

  split_randomized_action: {
    name: 'A/B Test',
    analyticsName: 'A/B Test',
    icon: 'a-b-test',
    showsCustomName: false,
    activePhraseShort: 'test and send',
    editSidebarName: 'Edit A/B Test',
    nameRequired: false,
  },
  conditional_branch_action: {
    name: 'True/False Branch',
    analyticsName: 'TF Branch',
    icon: 'branch-two',
    group: 'flow_control',
    showsCustomName: true,
    activePhraseShort: '',
    nameOptional: true,
  },
  multi_split_branch_action: {
    name: 'Multi-Split Branch',
    analyticsName: 'Multi-Split Branch',
    icon: 'branch-three',
    group: 'flow_control',
    showsCustomName: true,
    activePhraseShort: '',
    nameOptional: true,
  },
  multi_lang_branch_action: {
    name: 'Multi-Language Branch',
    analyticsName: 'Multi-Lang Branch',
    icon: 'localization',
    group: 'flow_control',
    showsCustomName: true,
    activePhraseShort: '',
    nameOptional: true,
  },
  random_cohort_branch_action: {
    name: 'Random Cohort Branch',
    analyticsName: 'Random Cohort Branch',
    icon: 'random-cohort',
    group: 'flow_control',
    showsCustomName: true,
    activePhraseShort: '',
    nameOptional: true,
  },
  experiment_action: {
    name: 'Experiment',
    analyticsName: 'Experiment',
    icon: 'a-b-test',
    group: 'flow_control',
    showsCustomName: true,
    activePhraseShort: '',
    nameOptional: true,
  },
  exit_action: {
    name: 'Exit',
    analyticsName: 'Exit',
    icon: 'exit',
    group: 'flow_control',
    showsCustomName: false,
    activePhraseShort: '',
    editSidebarName: 'Delete Exit',
  },
} satisfies Record<KnownActionType, ActionDisplayInfo>;

/** Ember's `DEFAULT` info for a type the table does not know. */
const UNKNOWN_ACTION_INFO: ActionDisplayInfo = {
  name: 'Unknown',
  analyticsName: 'Unknown',
  icon: 'help',
  showsCustomName: false,
  activePhraseShort: '',
};

/**
 * The known action type a wire `type` names, or null.
 *
 * The per-type card copy switches exhaustively over `KnownActionType`, so the
 * open wire string is resolved to a member of the union first; an unknown type
 * still gets a plain card, the way it gets Ember's `Unknown` info.
 */
export function knownActionType(type: string): KnownActionType | null {
  return KNOWN_ACTION_TYPES.find((known) => known === type) ?? null;
}

/**
 * Display metadata for an action type.
 *
 * Takes any string: the backend can add an action type this build has never
 * heard of, and such an action still needs a card. It gets Ember's `Unknown`
 * entry, exactly as it does on the Ember canvas.
 */
export function actionDisplayInfo(type: string): ActionDisplayInfo {
  const known = knownActionType(type);
  return known === null ? UNKNOWN_ACTION_INFO : ACTION_INFO[known];
}

/** What an action's type and subtype say about it. */
export type ActionKind = {
  type: string;
  sub_type?: string | null;
};

/**
 * The entries a subtype changes, keyed `type:sub_type`. A subtype not listed
 * here describes itself as its type does.
 */
const SUB_TYPE_INFO = new Map<string, Partial<ActionDisplayInfo>>([
  [
    'in_app_action:survey',
    { name: 'Survey', analyticsName: 'Survey', placeholder: 'Survey Message', nameRequired: true },
  ],
  [
    'in_app_action:tour',
    { name: 'Product Tour', analyticsName: 'Product Tour', placeholder: 'Product Tour', nameRequired: true },
  ],
  [
    'twilio_action:whatsapp',
    {
      name: 'WhatsApp',
      analyticsName: 'WhatsApp',
      icon: 'brand/whatsapp',
      placeholder: 'WhatsApp Message',
      nameRequired: true,
    },
  ],
  [
    'attribute_update_action:journey_attr_update',
    {
      name: 'Set Journey Attributes',
      analyticsName: 'Set Journey Attributes',
      icon: 'campaigns',
      activePhraseShort: 'set journey attributes',
      editSidebarName: 'Set Journey Attributes',
      placeholder: 'Set Journey Attributes',
      nameRequired: true,
    },
  ],
]);

/**
 * Display metadata for an action, with its subtype's changes applied: an
 * in-app survey is a `Survey`, and a journey attribute update is
 * `Set Journey Attributes` rather than `Create or Update Person`.
 */
export function actionInfo(action: ActionKind): ActionDisplayInfo {
  const base = actionDisplayInfo(action.type);
  const subTypeInfo = SUB_TYPE_INFO.get(`${action.type}:${action.sub_type ?? 'default'}`);
  return subTypeInfo === undefined ? base : { ...base, ...subTypeInfo };
}

/** Ember's `info.shortName` per action type: the noun a journey step names its action by. */
const ACTION_SHORT_NAMES = new Map([
  ['conditional_wait_action', 'wait until'],
  ['delay_seconds_action', 'time delay'],
  ['delay_time_window_action', 'time window'],
  ['grace_period_action', 'grace period'],
  ['randomized_delay_action', 'randomized delay'],
  ['email_action', 'email'],
  ['slack_action', 'Slack message'],
  ['twilio_action', 'SMS'],
  ['sms_action', 'SMS'],
  ['whatsapp_action', 'WhatsApp'],
  ['line_action', 'LINE'],
  ['inbox_action', 'Inbox Message'],
  ['push_action', 'push'],
  ['live_notification_action', 'live notification'],
  ['urban_airship_action', 'UA push'],
  ['in_app_action', 'in-app'],
  ['webhook_action', 'webhook'],
  ['attribute_update_action', 'create or update person'],
  ['static_seg_update_action', 'segment update'],
  ['collection_query_action', 'query collection'],
  ['query_objects_action', 'query objects'],
  ['create_event_action', 'event'],
  ['batch_update_action', 'update'],
  ['llm_action', 'llm'],
  ['split_randomized_action', 'test'],
  ['conditional_branch_action', 'True/False Branch'],
  ['multi_split_branch_action', 'Multi-Split Branch'],
  ['multi_lang_branch_action', 'Multi-Lang Branch'],
  ['random_cohort_branch_action', 'Random Cohort Branch'],
  ['experiment_action', 'experiment'],
  ['exit_action', 'Exit'],
]);

/** The short names a subtype changes, keyed `type:sub_type`, as Ember's `actionInfo` applies them. */
const SUB_TYPE_SHORT_NAMES = new Map<string, string>([
  ['in_app_action:survey', 'survey'],
  ['in_app_action:tour', 'product tour'],
  ['twilio_action:whatsapp', 'whatsapp'],
  ['attribute_update_action:journey_attr_update', 'set journey attributes'],
]);

/** An action's kind, with the platform a push action sends to. */
export type ShortNameActionKind = ActionKind & { send_to_platform?: string | null };

/**
 * The action's short name as Ember's `action.info.shortName` gives it, subtype and web push
 * included: `email`, `time delay`, `web push`. An unknown type is Ember's `unknown`.
 */
export function actionShortName(action: ShortNameActionKind): string {
  if (action.type === 'push_action' && action.send_to_platform === 'web') return 'web push';
  return (
    SUB_TYPE_SHORT_NAMES.get(`${action.type}:${action.sub_type ?? 'default'}`) ??
    ACTION_SHORT_NAMES.get(action.type) ??
    'unknown'
  );
}
